import { useNavigate, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Logo } from "@/components/landing/Logo";
import { GoogleIcon } from "@/components/landing/Header";
import { useAuth } from "@/context/AuthContext";
import { supabase } from "@/lib/supabase";
import { FullScreenLoader } from "@/components/FullScreenLoader";

export default function Login() {
  const navigate = useNavigate();
  const { status, user } = useAuth();

  if (status === "initializing") return <FullScreenLoader label="Loading" />;
  if (status === "authenticated") {
    return <Navigate to="/homepage" replace />;
  }

  const handleGoogleLogin = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (error) toast.error(error.message || "Could not start Google sign-in.");
  };

  return (
    <main
      className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#0A0A0A] px-6 font-body"
      data-testid="login-page"
    >
      {/* ambient glow */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute left-1/2 top-1/2 h-[560px] w-[560px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(circle,rgba(229,37,37,0.14),transparent_62%)] blur-3xl"
      />

      {/* back to home */}
      <button
        data-testid="login-back-btn"
        onClick={() => navigate("/")}
        className="absolute left-6 top-6 text-sm font-medium text-zinc-500 transition-colors duration-300 hover:text-white"
      >
        ← Back
      </button>

      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 w-full max-w-md text-center"
      >
        <div className="flex justify-center">
          <Logo className="text-4xl" />
        </div>

        <h1 className="font-heading mt-10 text-3xl font-bold tracking-tight text-white">
          Welcome back
        </h1>
        <p className="mt-3 text-base font-light text-zinc-400">
          Sign in to match with a rep and start practising.
        </p>

        <button
          data-testid="continue-with-google-btn"
          onClick={handleGoogleLogin}
          className="group mt-10 inline-flex w-full items-center justify-center gap-3 rounded-full bg-white px-8 py-4 text-base font-semibold text-black transition-[background-color,transform] duration-300 hover:bg-zinc-200 active:scale-[0.98]"
        >
          <GoogleIcon size={20} />
          Continue with Google
        </button>

        <p className="mx-auto mt-8 max-w-xs text-xs leading-relaxed text-zinc-600">
          By continuing, you agree to our{" "}
          <span className="text-zinc-400 underline-offset-2 hover:underline">
            Terms of Service
          </span>{" "}
          and{" "}
          <span className="text-zinc-400 underline-offset-2 hover:underline">
            Privacy Policy
          </span>
          .
        </p>
      </motion.div>
    </main>
  );
}
